import { Box, Title, Group, Select, ActionIcon, Button, Paper, Text, Flex, Stack, Alert } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconGripVertical, IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';
import { SectionProps, OrderByItem, FieldType } from '../../types';
import classes from './OrderBy.module.css';
import { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

type SortableOrderByItemProps = {
  id: string;
  orderBy: OrderByItem;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onUpdate: ( index: number, newOrderBy: OrderByItem ) => void;
  getAvailableFields: () => FieldType[];
  isRemoveDisabled: boolean;
};

function SortableOrderByItem({ id, orderBy, index, isEditing, onEdit, onRemove, onUpdate, getAvailableFields, isRemoveDisabled }: SortableOrderByItemProps ) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [ editingValues, setEditingValues ] = useState<OrderByItem>( orderBy );
  const { t } = useTranslation( 'widgets' );

  const onFinishEdit = () => {
    onUpdate( index, editingValues );
  };

  const style = {
    transform: CSS.Transform.toString( transform ),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const directionOptions = [
    { value: 'asc', label: i18n.t( 'widget_builder_order_by_direction_asc',{ ns:'widgets', defaultValue:'Ascending' }) },
    { value: 'desc', label: i18n.t( 'widget_builder_order_by_direction_desc',{ ns:'widgets', defaultValue:'Descending' }) }
  ];

  const getDirectionLabel = ( directionValue: string ) => {
    const direction = directionOptions.find( dir => dir.value === directionValue );
    return direction ? direction.label : directionValue;
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      p="sm"
      withBorder={false}
      mb="xs"
      radius="xs"
      className={classes.orderByPaper}
    >
      {isEditing ? (
        <Group className={classes.editForm}>
          <Select
            label={t( 'widget_builder_order_by_field_label','Field' )}
            placeholder={t( 'widget_builder_order_by_field_placeholder','Select field' )}
            data={getAvailableFields()}
            value={editingValues.field}
            allowDeselect={false}
            onChange={( value ) => setEditingValues({ ...editingValues, field: value || '' })}
            className={classes.fieldSelect}
            checkIconPosition="right"
          />
          <Select
            label={t( 'widget_builder_order_by_direction_label','Direction' )}
            placeholder={t( 'widget_builder_order_by_direction_placeholder','Select direction' )}
            data={directionOptions}
            value={editingValues.direction}
            allowDeselect={false}
            onChange={( value ) => setEditingValues({ ...editingValues, direction: value as 'asc' | 'desc' })}
            className={classes.directionSelect}
            checkIconPosition="right"
          />
          <Button onClick={onFinishEdit} disabled={!editingValues.field || !editingValues.direction} mt={24}>
            {t( 'widget_builder_order_by_done_button','Done' )}
          </Button>
        </Group>
      ) : (
        <Flex justify="space-between" align="center" gap="md">
          <div {...attributes} {...listeners} className={classes.dragHandle}>
            <IconGripVertical size={16} stroke={1.5} />
          </div>
          <div className={classes.orderByInfo}>
            <Text size="sm">
              {orderBy.field.split( '.' )[1] || orderBy.field} {getDirectionLabel( orderBy.direction )}
            </Text>
          </div>
          <Group>
            <ActionIcon color="primary.6" variant="subtle" onClick={onEdit}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon color="danger.6" variant="subtle" onClick={onRemove} disabled={isRemoveDisabled}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Flex>
      )}
    </Paper>
  );
}

export default function OrderBySection({ getAvailableFields, updateConfig, config, validationErrors }: SectionProps ) {
  const { t } = useTranslation( 'widgets' );
  const orderBy = config.orderBy || [];
  const [ showAddForm, setShowAddForm ] = useState( false );
  const [ editingIndex, setEditingIndex ] = useState<number | null>( null );
  const [ newOrderBy, setNewOrderBy ] = useState<OrderByItem>({ field: '', direction: 'asc' });

  const sectionErrors = validationErrors?.orderBy;

  const addOrderBy = () => {
    const newOrderByList = [ ...orderBy, { ...newOrderBy }];
    updateConfig( 'orderBy', newOrderByList );
    setNewOrderBy({ field: '', direction: 'asc' });
    setShowAddForm( false );
  };

  const removeOrderBy = ( index: number ) => {
    const newOrderByList = orderBy.filter(( _, i ) => i !== index );
    updateConfig( 'orderBy', newOrderByList );
    if ( editingIndex === index ) setEditingIndex( null );
  };

  const updateOrderByItem = ( index: number, newOrderByItem: OrderByItem ) => {
    const newOrderByList = [...orderBy];
    newOrderByList[index] = { ...newOrderByList[index], ...newOrderByItem };
    updateConfig( 'orderBy', newOrderByList );
    setEditingIndex( null );
  };

  const updateNewOrderBy = ( field: keyof OrderByItem, value: any ) => {
    setNewOrderBy( prev => ({ ...prev, [field]: value }));
  };

  const sensors = useSensors(
    useSensor( PointerSensor ),
    useSensor( KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = ( event: any ) => {
    const { active, over } = event;
    if ( active.id !== over.id ) {
      const oldIndex = orderBy.findIndex(( _, i ) => `orderBy-${i}` === active.id );
      const newIndex = orderBy.findIndex(( _, i ) => `orderBy-${i}` === over.id );
      updateConfig( 'orderBy', arrayMove( orderBy, oldIndex, newIndex ));
    }
  };

  const directionOptions = [
    { value: 'asc', label: t( 'widget_builder_order_by_direction_asc','Ascending' ) },
    { value: 'desc', label: t( 'widget_builder_order_by_direction_desc','Descending' ) }
  ];

  return (
    <div className={classes.container}>
      <Stack gap="md">
        <Title order={4} className={classes.sectionTitle}>
          {t( 'widget_builder_order_by_title','Order By' )}
        </Title>
        <Alert icon={<IconInfoCircle size="1.2rem" />} color="blue.5" variant="light" mb="xs">
          <Text size='sm'>
            {t( 'widget_builder_order_by_description','Define the order in which your data should be displayed. You can sort by any field in your data source.' )}
          </Text>
        </Alert>

        {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
          <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
            <Text size='sm' c="red.9">
              {t( 'widget_builder_order_by_errors_message','Please fix the following errors:' )}
              <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                {Object.entries( sectionErrors ).map(([ field, error ]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Text>
          </Alert>
        )}

        <Box className={classes.content}>
          {orderBy.length > 0 && (
            <div className={classes.orderByListContainer}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderBy.map(( _, i ) => `orderBy-${i}` )} strategy={verticalListSortingStrategy}>
                  {orderBy.map(( item, index ) => (
                    <SortableOrderByItem
                      key={index}
                      id={`orderBy-${index}`}
                      orderBy={item}
                      index={index}
                      isEditing={editingIndex === index}
                      onEdit={() => setEditingIndex( index )}
                      onRemove={() => removeOrderBy( index )}
                      onUpdate={updateOrderByItem}
                      getAvailableFields={getAvailableFields}
                      isRemoveDisabled={false}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {showAddForm ? (
            <Paper p="md" className={classes.addForm} bg={'gray.0'}>
              <Group className={classes.itemRow}>
                <Select
                  label={t( 'widget_builder_order_by_field_label','Field' )}
                  placeholder={t( 'widget_builder_order_by_field_placeholder','Select field' )}
                  data={getAvailableFields()}
                  value={newOrderBy.field}
                  onChange={( value ) => updateNewOrderBy( 'field', value )}
                  className={classes.fieldSelect}
                  checkIconPosition="right"
                />
                <Select
                  label={t( 'widget_builder_order_by_direction_label','Direction' )}
                  placeholder={t( 'widget_builder_order_by_direction_placeholder','Select direction' )}
                  data={directionOptions}
                  value={newOrderBy.direction}
                  onChange={( value ) => updateNewOrderBy( 'direction', value as 'asc' | 'desc' )}
                  className={classes.directionSelect}
                  checkIconPosition="right"
                />
              </Group>
              <Group justify="flex-end" mt="md">
                <Button variant="outline" bg={'white'} onClick={() => setShowAddForm( false )}>
                  {t( 'widget_builder_order_by_cancel_button','Cancel' )}
                </Button>
                <Button onClick={addOrderBy} disabled={!newOrderBy.field}>
                  {t( 'widget_builder_order_by_add_button','Add' )}
                </Button>
              </Group>
            </Paper>
          ) : (
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() => setShowAddForm( true )}
              className={classes.addButton}
              fullWidth
            >
              {t( 'widget_builder_order_by_add_order_by_button','Add Order By' )}
            </Button>
          )}
        </Box>
      </Stack>
    </div>
  );
}
